import Table from 'components/commercetools-ui/atoms/table';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CT_STOREKEY } from 'helpers/constants/envVariables';
import { desktop } from 'helpers/utils/screensizes';
import { useFormat } from 'helpers/hooks/useFormat';

import Link from 'next/link';
import { useParams } from 'next/navigation';
import * as React from 'react';
import { useEffect, useState } from 'react';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { localeMapping } from 'helpers/constants/localeMapping';
import { getPdpUrl, getPdpUrlWithLocale } from 'helpers/utils/pdpUrlHelper';
import { fetchBundleProducts } from 'helpers/next-services';
 
interface bundleAttributes {
  sparePartsReferences: any;
  assetsAttributes?: Array<{ productname: string; code: string; image: string; inventory: string }>;
}
 
const SpareParts = ({ sparePartsReferences }: bundleAttributes) => {
  const [isDesktop] = useMediaQuery(desktop);
  const [sparePartsData, setSparePartsData] = useState<any[]>([]);
  const { locale } = useParams();
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const localeKey = localeMapping[locale];

  const handleSpareParts = async () => {
    const requestData = {
      variant: sparePartsReferences,
      storeKey: CT_STOREKEY + locale
    };

    try {
      const responseBody = await fetchBundleProducts({ ...requestData, locale: locale as string });
      if (responseBody && responseBody.length > 0) {
        setSparePartsData(responseBody);
      } else {
        setSparePartsData([]);
      }
    } catch (error) {
      console.error('Spare Parts Data Fetch Error:', error);
      setSparePartsData([]);
    }
  };

  useEffect(() => {
    handleSpareParts();
  }, [sparePartsReferences]);

  if (!Array.isArray(sparePartsData) || sparePartsData.length === 0) {
    return null;
  }

  return (
    <div id="spare-parts-section" className="mb-40">
      <div className="2xl:pt-80 xl:pt-80 px-24 md:px-28 2xl:pl-0 2xl:pr-80 xl:pr-60 lg:pl-30 xl:pl-0 lg:pt-48 lg:pr-48 pt-48 md:pl-28 pl-24 pr-24">
        <Typography className="mb-20 text-32 !font-medium leading-8 text-[#090909] xl:text-36 2xl:text-48 md:leading-[3.25rem]">
          {formatProductMessage({ id: 'product.spare.parts', defaultMessage: 'Spare Parts' })}
        </Typography>
      </div>
      <div className="px-24 pl-24 pr-24 md:px-28 md:pl-28 lg:pl-30 lg:pr-48 xl:pl-0 xl:pr-60 2xl:pl-0 2xl:pr-80 ">
        {!isDesktop ? (
          <>
            {sparePartsData.map((item) => (
                <div className="" key={item?.id}>
                  <Link className="flex items-center gap-20" 
                  href={getPdpUrlWithLocale(locale, item?.Slug[localeKey], item?.SKU, item?.Colour)}
                  prefetch={true}>
                    <img
                      src={`${
                        item?.Images?.[0]?.url
                          ? item?.Images?.[0]?.url
                          : '/images/no-image-placeholder/product-thumbnail.jpg'
                      }`}
                      alt=""
                      className="my-10 border-b border-white w-80 lg:w-60 2xl:w-80 cursor-pointer"
                    />
                    <div className="flex flex-col">
                      <div className="pt-16 pb-2 text-16 font-medium leading-4 text-[#090909] capitalize">
                        {item?.Name?.[`${localeKey}`]}
                      </div>
                      <div className="flex justify-between">
                        <div className="py-6">
                          <div className="flex gap-1">
                            <span className="text-12 leading-4 text-[#666666]">
                              {formatProductMessage({ id: 'product.item.code', defaultMessage: 'Item Code' })}:
                            </span>
                            <span className="text-12 leading-4"> {item?.ItemCode}</span>
                          </div>
                          <div className="flex gap-1">
                            <span className="text-12 leading-4 text-[#666666]">
                              {formatProductMessage({ id: 'product.rrp.price', defaultMessage: 'RRP' })}
                              {formatProductMessage({ id: 'product.inc.tax', defaultMessage: '(inc. GST)' })}:
                            </span>
                            <span className="text-12 leading-4 ">
                              {/* Displaying spare price based on country */}
                              {'$' + ((item?.Prices?.find((obj: { country: string; }) => obj.country == locale.toUpperCase())?.value?.centAmount as number) / 100).toFixed(2)}
                            </span>
                          <span className="text-12 leading-4">
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <hr />
                </div>
              ))
            }
          </>
        ) : (
          <div className="">
            <Table.Container className="!border-[#CCCCCC]">
                <Table.Head>
                  <tr className="border-b px-4 py-2 text-left">
                    <Table.Cell> </Table.Cell>
                    <Table.Cell>
                      <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                        {formatProductMessage({ id: 'product.productpartname', defaultMessage: 'Part name' })}
                      </Typography>{' '}
                    </Table.Cell>
                    <Table.Cell>
                      <Typography className="whitespace-nowrap text-16 !font-medium leading-6 text-[#090909]">
                        {formatProductMessage({ id: 'product.number', defaultMessage: 'Product number' })}
                      </Typography>
                    </Table.Cell>
                    <Table.Cell>
                      <Typography className="whitespace-nowrap text-16 !font-medium leading-6 text-[#090909]">
                        {formatProductMessage({ id: 'product.rrp.price', defaultMessage: 'RRP' })}
                      </Typography>
                    </Table.Cell>
                  </tr>
                </Table.Head>
                <Table.Body>
                  
                    {sparePartsData?.map((item) => (
                      <>
                      <Link 
                      href={getPdpUrlWithLocale(locale, item?.Slug[localeKey], item?.SKU, item?.Colour)}
                      className="contents">
                        <Table.Row key={item?.id as string} className="whitespace-normal h-80 border-b border-gray-300 align-middle">
                          <Table.Cell className="!p-0 lg:w-80 2xl:w-100">
                            <img
                              src={`${item?.Images?.[0]?.url?item?.Images?.[0]?.url:'/images/no-image-placeholder/product-thumbnail.jpg'}`}
                              alt=''
                              className="cursor-pointer border-b border-white lg:w-60 2xl:w-80"
                            />
                          </Table.Cell>
                          <Table.Cell className="whitespace-normal">
                            <Typography
                              className="text-16 !font-medium leading-6 text-[#090909] capitalize"
                            >
                              {item?.Name?.[`${localeKey}`]}
                            </Typography>
                          </Table.Cell>
                          <Table.Cell>
                            <Typography className="whitespace-nowrap text-16 !font-medium leading-6 text-[#090909]">
                              {item?.ItemCode}
                            </Typography>
                          </Table.Cell>
                          <Table.Cell>
                              <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                                {/* Displaying spare price based on country */}
                                {'$' + ((item?.Prices?.find((obj: { country: string; }) => obj.country == locale.toUpperCase())?.value?.centAmount as number) / 100).toFixed(2)}
                              </Typography>              
                              <Typography as="span">{formatProductMessage({ id: 'product.inc.tax', defaultMessage: '(inc. GST)' })}</Typography>
                          </Table.Cell>
                        </Table.Row>
                      </Link>
                      </>
                    ))}
                </Table.Body>
              </Table.Container>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpareParts;